import { ThemeProvider } from "@mui/material/styles"
import { MsalProvider } from "@azure/msal-react"
import { useEffect, useState } from "react"
import { Navigate, Route, Routes } from "react-router-dom"
import "./App.scss"
import { muiTheme } from "./app/components/muiTheme"
import { AppContextProvider } from "./app/context/AppContext"
import { UserContextProvider } from "./app/context/UserContext"
import { AccessLevel, DimaFeatureShortNameEnum } from "./app/models/Authorization"
import { Layout } from "./app/pages/Layout"
import { AppRegistrationsContainer } from "./app/pages/app_registrations_page/AppRegistrationsContainer"
import { AppRegistrationContainer } from "./app/pages/app_registrations_page/app_registrations_tab/AppRegistrationContainer"
import { AppRegistrationCreate } from "./app/pages/app_registrations_page/app_registrations_tab/AppRegistrationCreate"
import { ApplicationsContainer } from "./app/pages/applications_page/ApplicationsContainer"
import { ApplicationContainer } from "./app/pages/applications_page/applications_tab/ApplicationContainer"
import { ApplicationKeyValuePairContainer } from "./app/pages/applications_page/applications_tab/key_value_pairs_tab/ApplicationKeyValuePairContainer"
import { ApplicationKeyValuePairCreate } from "./app/pages/applications_page/applications_tab/key_value_pairs_tab/ApplicationKeyValuePairCreate"
import { ApplicationPermissionContainer } from "./app/pages/applications_page/applications_tab/permissions_tab/ApplicationPermissionContainer"
import { ApplicationPermissionCreate } from "./app/pages/applications_page/applications_tab/permissions_tab/ApplicationPermissionCreate"
import { ApplicationPermissionFirmwareConstraintContainer } from "./app/pages/applications_page/applications_tab/permissions_tab/fw_constraints_tab/ApplicationPermissionFirmwareConstraintContainer"
import { ApplicationPermissionFirmwareConstraintCreate } from "./app/pages/applications_page/applications_tab/permissions_tab/fw_constraints_tab/ApplicationPermissionFirmwareConstraintCreate"
import { ApplicationPermissionFirmwareConstraintRoleCreate } from "./app/pages/applications_page/applications_tab/permissions_tab/fw_constraints_tab/roles_tab/ApplicationPermissionFirmwareConstraintRoleCreate"
import { ApplicationPropertyContainer } from "./app/pages/applications_page/applications_tab/properties_tab/ApplicationPropertyContainer"
import { ApplicationPropertyCreate } from "./app/pages/applications_page/applications_tab/properties_tab/ApplicationPropertyCreate"
import { ApplicationRoleContainer } from "./app/pages/applications_page/applications_tab/roles_tab/ApplicationRoleContainer"
import { ApplicationRoleCreate } from "./app/pages/applications_page/applications_tab/roles_tab/ApplicationRoleCreate"
import { ApplicationRoleApproverContainer } from "./app/pages/applications_page/applications_tab/roles_tab/approvers_tab/ApplicationRoleApproverContainer"
import { ApplicationRoleApproverCreate } from "./app/pages/applications_page/applications_tab/roles_tab/approvers_tab/ApplicationRoleApproverCreate"
import { ApplicationRoleApproverPropertyContainer } from "./app/pages/applications_page/applications_tab/roles_tab/approvers_tab/properties_tab/ApplicationRoleApproverPropertyContainer"
import { ApplicationRolePermissionContainer } from "./app/pages/applications_page/applications_tab/roles_tab/permissions_tab/ApplicationRolePermissionContainer"
import { ApplicationRolePermissionCreate } from "./app/pages/applications_page/applications_tab/roles_tab/permissions_tab/ApplicationRolePermissionCreate"
import { ApplicationRolePermissionPropertyCreate } from "./app/pages/applications_page/applications_tab/roles_tab/permissions_tab/properties_tab/ApplicationRolePermissionPropertyCreate"
import { Logout } from "./app/pages/auth/Logout"
import { DeprecatedDevicesContainer } from "./app/pages/deprecated_devices_page/DeprecatedDevicesContainer"
import { DeprecatedDeviceContainer } from "./app/pages/deprecated_devices_page/deprecated_devices_tab/DeprecatedDeviceContainer"
import { DeprecatedDeviceCreate } from "./app/pages/deprecated_devices_page/deprecated_devices_tab/DeprecatedDeviceCreate"
import { DevicesContainer } from "./app/pages/devices_page/DevicesContainer"
import { CategoryContainer } from "./app/pages/devices_page/categories_tab/CategoryContainer"
import { CategoryCreate } from "./app/pages/devices_page/categories_tab/CategoryCreate"
import { DeviceContainer } from "./app/pages/devices_page/devices_tab/DeviceContainer"
import { DeviceCreate } from "./app/pages/devices_page/devices_tab/DeviceCreate"
import { DeviceRoleContainer } from "./app/pages/devices_page/devices_tab/roles_tab/DeviceRoleContainer"
import { DeviceRoleCreate } from "./app/pages/devices_page/devices_tab/roles_tab/DeviceRoleCreate"
import { DeviceRolePermissionContainer } from "./app/pages/devices_page/devices_tab/roles_tab/permissions_tab/DeviceRolePermissionContainer"
import { DeviceRolePermissionCreate } from "./app/pages/devices_page/devices_tab/roles_tab/permissions_tab/DeviceRolePermissionCreate"
import { DeviceRolePermissionApplicationRoleCreate } from "./app/pages/devices_page/devices_tab/roles_tab/permissions_tab/applicationRoles_tab/DeviceRolePermissionApplicationRoleCreate"
import { DeviceSoftwarePackageFirmwareConstraintContainer } from "./app/pages/devices_page/devices_tab/software_package_firmware_constraints_tab/DeviceSoftwarePackageFirmwareConstraintContainer"
import { DeviceSoftwarePackageFirmwareConstraintCreate } from "./app/pages/devices_page/devices_tab/software_package_firmware_constraints_tab/DeviceSoftwarePackageFirmwareConstraintCreate"
import { FamilyContainer } from "./app/pages/devices_page/families_tab/FamilyContainer"
import { FamilyCreate } from "./app/pages/devices_page/families_tab/FamilyCreate"
import { FactoryReportsContainer } from "./app/pages/factory_reports_page/FactoryReportsContainer"
import { FactoryContainer } from "./app/pages/factory_reports_page/factories_tab/FactoryContainer"
import { FactoryCreate } from "./app/pages/factory_reports_page/factories_tab/FactoryCreate"
import { FeedbacksContainer } from "./app/pages/feedbacks_page/FeedbacksContainer"
import { FeedbackContainer } from "./app/pages/feedbacks_page/feedbacks_tab/FeedbackContainer"
import { FeedbackTopicContainer } from "./app/pages/feedbacks_page/topics_tab/FeedbackTopicContainer"
import { FeedbackTopicCreate } from "./app/pages/feedbacks_page/topics_tab/FeedbackTopicCreate"
import { FilesContainer } from "./app/pages/files_page/FilesContainer"
import { FileContainer } from "./app/pages/files_page/files_tab/FileContainer"
import { FileCreate } from "./app/pages/files_page/files_tab/FileCreate"
import { FileVersionContainer } from "./app/pages/files_page/files_tab/versions_tab/FileVersionContainer"
import { FileVersionCreate } from "./app/pages/files_page/files_tab/versions_tab/FileVersionCreate"
import { GatewayResetPassword } from "./app/pages/gateway_reset_password_page/GatewayResetPassword"
import { GatewaysContainer } from "./app/pages/gateways_page/GatewaysContainer"
import { GatewaysManufacturedDeviceContainer } from "./app/pages/gateways_page/supplier_tab/GatewaysManufacturedDeviceContainer"
import { IssueReportsContainer } from "./app/pages/issue_reports_page/IssueReportsContainer"
import { IssueReportContainer } from "./app/pages/issue_reports_page/issue_reports_tab/IssueReportContainer"
import { IssueReportTopicContainer } from "./app/pages/issue_reports_page/topics_tab/IssueReportTopicContainer"
import { IssueReportTopicCreate } from "./app/pages/issue_reports_page/topics_tab/IssueReportTopicCreate"
import { LitePanelProResetPassword } from "./app/pages/litePanelPro_reset_password_page/LitePanelProResetPassword"
import { NotificationsContainer } from "./app/pages/notifications_page/NotificationsContainer"
import { NotificationContainer } from "./app/pages/notifications_page/notifications_tab/NotificationContainer"
import { NotificationCreate } from "./app/pages/notifications_page/notifications_tab/NotificationCreate"
import { TopicContainer } from "./app/pages/notifications_page/topics_tab/TopicContainer"
import { TopicCreate } from "./app/pages/notifications_page/topics_tab/TopicCreate"
import { ProductionsContainer } from "./app/pages/productions_page/ProductionsContainer"
import { KeyValuePairContainer } from "./app/pages/productions_page/keyValuePairs_tab/KeyValuePairContainer"
import { KeyValuePairCreate } from "./app/pages/productions_page/keyValuePairs_tab/KeyValuePairCreate"
import { RoleRequestsContainer } from "./app/pages/role_requests_page/RoleRequestsContainer"
import { RoleRequestContainer } from "./app/pages/role_requests_page/role_requests_tab/RoleRequestContainer"
import { SettingsContainer } from "./app/pages/settings_page/SettingsContainer"
import { SupplierContainer } from "./app/pages/supplier_portal_page/SupplierContainer"
import { ManufacturedDeviceContainer } from "./app/pages/supplier_portal_page/supplier_tab/ManufacturedDeviceContainer"
import { ObjectIdContainer } from "./app/pages/supplier_portal_page/supplier_tab/ObjectIdContainer"
import { TrainingsContainer } from "./app/pages/trainings_page/TrainingsContainer"
import { TrainingContainer } from "./app/pages/trainings_page/trainings_tab/TrainingContainer"
import { TrainingCourseContainer } from "./app/pages/trainings_page/trainings_tab/courses_tab/TrainingCourseContainer"
import { TrainingCourseCreate } from "./app/pages/trainings_page/trainings_tab/courses_tab/TrainingCourseCreate"
import { TrainingCreate } from "./app/pages/trainings_page/trainings_tab/TrainingCreate"
import { TrainingPermissionCreate } from "./app/pages/trainings_page/trainings_tab/permissions_tab/TrainingPermissionCreate"
import { UsersContainer } from "./app/pages/users_page/UsersContainer"
import { UserContainer } from "./app/pages/users_page/users_tab/UserContainer"
import { UserCreate } from "./app/pages/users_page/users_tab/UserCreate"
import { UserApplicationPermissionContainer } from "./app/pages/users_page/users_tab/apps_permissions_tab/UserApplicationPermissionContainer"
import { UserApplicationsPermissionCreate } from "./app/pages/users_page/users_tab/apps_permissions_tab/UserApplicationsPermissionCreate"
import { UserApplicationPermissionPropertyCreate } from "./app/pages/users_page/users_tab/apps_permissions_tab/properties_tab/UserApplicationPermissionPropertyCreate"
import { UserApplicationRoleContainer } from "./app/pages/users_page/users_tab/apps_roles_tab/UserApplicationRoleContainer"
import { UserApplicationsRoleCreate } from "./app/pages/users_page/users_tab/apps_roles_tab/UserApplicationsRoleCreate"
import { UserApplicationRolePropertyCreate } from "./app/pages/users_page/users_tab/apps_roles_tab/properties_tab/UserApplicationRolePropertyCreate"
import { RequireAuth } from "./app/routing/RequireAuth"
import { RequireFeatureAccessLevel } from "./app/routing/RequireFeatureAccessLevel"
import { getMsalInstance } from "./msalInstance"
import { HomeContainer } from "./app/pages/home/HomeContainer"

// MSAL configuration
const msalInstance = getMsalInstance()

function App() {
  const [isMsalInitialized, setIsMsalInitialized] = useState(false)

  useEffect(() => {
    const initializeMsal = async () => {
      await msalInstance.initialize()
      setIsMsalInitialized(true)
    }
    initializeMsal()
  }, [])

  if (!isMsalInitialized) {
    return <div>Loading...</div>
  }

  return (
    <ThemeProvider theme={muiTheme}>
    <MsalProvider instance={msalInstance}>
      <Routes>
        <Route
          path="/"
          element={
            <RequireAuth>
              <AppContextProvider>
                <UserContextProvider>
                  <Layout />
                </UserContextProvider>
              </AppContextProvider>
            </RequireAuth>
          }
        >
          <Route index element={<HomeContainer />} />
          <Route
            path="applications"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationRoleCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationRoleContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId/permissions/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationRolePermissionCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId/permissions/:permissionId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationRolePermissionContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId/permissions/:permissionId/properties/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationRolePermissionPropertyCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/permissions/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationPermissionCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/permissions/:permissionId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationPermissionContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/permissions/:permissionId/firmware-constraints/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationPermissionFirmwareConstraintCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/permissions/:permissionId/firmware-constraints/:firmwareConstraintId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationPermissionFirmwareConstraintContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/permissions/:permissionId/firmware-constraints/:firmwareConstraintId/roles/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationPermissionFirmwareConstraintRoleCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/properties/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationPropertyCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/properties/:propertyName"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationPropertyContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId/approvers/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationRoleApproverCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId/approvers/:userId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationRoleApproverContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId/approvers/:userId/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationRoleApproverCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/roles/:roleId/approvers/:userId/properties/:propertyName/values/:propertyValue"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationRoleApproverPropertyContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/keyValuePairs/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Write}
              >
                <ApplicationKeyValuePairCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="applications/:applicationId/keyValuePairs/:keyValuePairId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <ApplicationKeyValuePairContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="notifications"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.NM}
                accessLevel={AccessLevel.Read}
              >
                <NotificationsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="notifications/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.NM}
                accessLevel={AccessLevel.Write}
              >
                <NotificationCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="notifications/:notificationId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.NM}
                accessLevel={AccessLevel.Read}
              >
                <NotificationContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="notifications/topics/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.NM}
                accessLevel={AccessLevel.Write}
              >
                <TopicCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="notifications/topics/:notificationTopicId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.NM}
                accessLevel={AccessLevel.Read}
              >
                <TopicContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="role-requests"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RR}
                accessLevel={AccessLevel.Read}
              >
                <RoleRequestsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="role-requests/:roleRequestId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RR}
                accessLevel={AccessLevel.Read}
              >
                <RoleRequestContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Read}
              >
                <DevicesContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Write}
              >
                <DeviceCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Read}
              >
                <DeviceContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId/roles/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Write}
              >
                <DeviceRoleCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId/roles/:roleId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Read}
              >
                <DeviceRoleContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId/roles/:roleId/permissions/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Write}
              >
                <DeviceRolePermissionCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId/roles/:roleId/permissions/:permissionId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Read}
              >
                <DeviceRolePermissionContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId/roles/:roleId/permissions/:permissionId/applicationRoles/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Write}
              >
                <DeviceRolePermissionApplicationRoleCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId/software-package-firmware-constraints/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Write}
              >
                <DeviceSoftwarePackageFirmwareConstraintCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/:deviceId/software-package-firmware-constraints/:softwarePackageFirmwareConstraintId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Read}
              >
                <DeviceSoftwarePackageFirmwareConstraintContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/families/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Write}
              >
                <FamilyCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/families/:familyId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Read}
              >
                <FamilyContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/categories/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Write}
              >
                <CategoryCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="devices/categories/:categoryId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DM}
                accessLevel={AccessLevel.Read}
              >
                <CategoryContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="files"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FM}
                accessLevel={AccessLevel.Read}
              >
                <FilesContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="files/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FM}
                accessLevel={AccessLevel.Write}
              >
                <FileCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="files/:fileId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FM}
                accessLevel={AccessLevel.Read}
              >
                <FileContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="files/:fileId/versions/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FM}
                accessLevel={AccessLevel.Write}
              >
                <FileVersionCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="files/:fileId/versions/:versionId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FM}
                accessLevel={AccessLevel.Read}
              >
                <FileVersionContainer />
              </RequireFeatureAccessLevel>
            }
          />
          {/*
            <Route
              path="files/groups/create"
              element={
                <RequireFeatureAccessLevel
                  feature={DimaFeatureShortNameEnum.FM}
                  accessLevel={AccessLevel.Write}
                >
                  <GroupCreate />
                </RequireFeatureAccessLevel>
              }
            />
            <Route
              path="files/groups/:groupId"
              element={
                <RequireFeatureAccessLevel
                  feature={DimaFeatureShortNameEnum.FM}
                  accessLevel={AccessLevel.Read}
                >
                  <GroupContainer />
                </RequireFeatureAccessLevel>
              }
            />
            <Route
              path="files/groups/:groupId/users/create"
              element={
                <RequireFeatureAccessLevel
                  feature={DimaFeatureShortNameEnum.FM}
                  accessLevel={AccessLevel.Write}
                >
                  <GroupUserCreate />
                </RequireFeatureAccessLevel>
              }
            />
            */}
          <Route
            path="factory-reports"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FR}
                accessLevel={AccessLevel.Read}
              >
                <FactoryReportsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="factory-reports/factories/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FR}
                accessLevel={AccessLevel.Write}
              >
                <FactoryCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="factory-reports/factories/:factoryId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FR}
                accessLevel={AccessLevel.Write}
              >
                <FactoryContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="production"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.PR}
                accessLevel={AccessLevel.Read}
              >
                <ProductionsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="production/key-values/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.PR}
                accessLevel={AccessLevel.Write}
              >
                <KeyValuePairCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="production/key-values/:productionKeyValuePairId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.PR}
                accessLevel={AccessLevel.Read}
              >
                <KeyValuePairContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="deprecated-devices"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DD}
                accessLevel={AccessLevel.Read}
              >
                <DeprecatedDevicesContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="deprecated-devices/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DD}
                accessLevel={AccessLevel.Write}
              >
                <DeprecatedDeviceCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="deprecated-devices/:deprecatedDeviceId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.DD}
                accessLevel={AccessLevel.Read}
              >
                <DeprecatedDeviceContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="feedbacks"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FB}
                accessLevel={AccessLevel.Read}
              >
                <FeedbacksContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="feedbacks/:feedbackId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FB}
                accessLevel={AccessLevel.Read}
              >
                <FeedbackContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="feedbacks/topics/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FB}
                accessLevel={AccessLevel.Write}
              >
                <FeedbackTopicCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="feedbacks/topics/:feedbackTopicId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.FB}
                accessLevel={AccessLevel.Read}
              >
                <FeedbackTopicContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="issue-reports"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.IR}
                accessLevel={AccessLevel.Read}
              >
                <IssueReportsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="issue-reports/:issueReportId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.IR}
                accessLevel={AccessLevel.Read}
              >
                <IssueReportContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="issue-reports/topics/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.IR}
                accessLevel={AccessLevel.Write}
              >
                <IssueReportTopicCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="issue-reports/topics/:issueReportTopicId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.IR}
                accessLevel={AccessLevel.Read}
              >
                <IssueReportTopicContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="trainings"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.TM}
                accessLevel={AccessLevel.Read}
              >
                <TrainingsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="trainings/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.TM}
                accessLevel={AccessLevel.Write}
              >
                <TrainingCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="trainings/:trainingId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.TM}
                accessLevel={AccessLevel.Read}
              >
                <TrainingContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="trainings/:trainingId/permissions/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.TM}
                accessLevel={AccessLevel.Write}
              >
                <TrainingPermissionCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="trainings/:trainingId/courses/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.TM}
                accessLevel={AccessLevel.Write}
              >
                <TrainingCourseCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="trainings/:trainingId/courses/:courseId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.TM}
                accessLevel={AccessLevel.Read}
              >
                <TrainingCourseContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RM}
                accessLevel={AccessLevel.Read}
              >
                <UsersContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RM}
                accessLevel={AccessLevel.Write}
              >
                <UserCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/:userId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RM}
                accessLevel={AccessLevel.Read}
              >
                <UserContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/:userId/apps-roles/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RM}
                accessLevel={AccessLevel.Write}
              >
                <UserApplicationsRoleCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/:userId/applications/:applicationId/roles/:roleId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RM}
                accessLevel={AccessLevel.Read}
              >
                <UserApplicationRoleContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/:userId/applications/:applicationId/roles/:roleId/properties/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <UserApplicationRolePropertyCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/:userId/apps-permissions/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RM}
                accessLevel={AccessLevel.Write}
              >
                <UserApplicationsPermissionCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/:userId/applications/:applicationId/permissions/:permissionId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RM}
                accessLevel={AccessLevel.Write}
              >
                <UserApplicationPermissionContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="users/:userId/applications/:applicationId/permissions/:permissionId/properties/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AM}
                accessLevel={AccessLevel.Read}
              >
                <UserApplicationPermissionPropertyCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="app-registrations"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AR}
                accessLevel={AccessLevel.Read}
              >
                <AppRegistrationsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="app-registrations/create"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AR}
                accessLevel={AccessLevel.Write}
              >
                <AppRegistrationCreate />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="app-registrations/:appRegistrationId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.AR}
                accessLevel={AccessLevel.Read}
              >
                <AppRegistrationContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="settings"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.RR}
                accessLevel={AccessLevel.Read}
              >
                <SettingsContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="supplierPortal"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.SP}
                accessLevel={AccessLevel.Write}
              >
                <SupplierContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="supplierPortal/manufacturedDevices/:deviceId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.SP}
                accessLevel={AccessLevel.Write}
              >
                <ManufacturedDeviceContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="supplierPortal/objectIdsRequests/:objectIdsRequestId/objectIds"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.SP}
                accessLevel={AccessLevel.Write}
              >
                <ObjectIdContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="gateways"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.GW}
                accessLevel={AccessLevel.Write}
              >
                <GatewaysContainer />
              </RequireFeatureAccessLevel>
            }
          />
          <Route
            path="gateways/manufacturedDevices/:deviceId"
            element={
              <RequireFeatureAccessLevel
                feature={DimaFeatureShortNameEnum.GW}
                accessLevel={AccessLevel.Write}
              >
                <GatewaysManufacturedDeviceContainer />
              </RequireFeatureAccessLevel>
            }
          />
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
        <Route path="logout" element={<Logout />} />
        <Route
          path="litePanelPro/resetPassword"
          element={
            <RequireAuth>
              <UserContextProvider>
                <LitePanelProResetPassword />
              </UserContextProvider>
            </RequireAuth>
          }
        />
        <Route
          path="gateway/resetPassword"
          element={
            <RequireAuth>
              <UserContextProvider>
                <GatewayResetPassword />
              </UserContextProvider>
            </RequireAuth>
          }
        />
      </Routes>
    </MsalProvider>
    </ThemeProvider>
  )
}

export default App
